import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Billboard } from '@react-three/drei';
import * as THREE from 'three';

// Reusable Particle System for both the main Accretion Disk and the Lensed Halo
const ParticleRing = ({ count = 5000, minRadius, maxRadius, minSpeed, maxSpeed, colorFunc, opacity = 0.8, vertical = false, ...props }) => {
  const mesh = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  
  const particles = useMemo(() => {
    const temp = [];
    for (let i = 0; i < count; i++) {
      // Distribution: biased towards the inner edge for the accretion disk look
      const rVal = Math.random();
      const radius = minRadius + (1 - Math.pow(rVal, 2)) * (maxRadius - minRadius);
      
      const angle = Math.random() * Math.PI * 2;
      // Keplerian-ish speed: faster near center
      const speedFactor = 1 / Math.sqrt(radius); 
      const speed = minSpeed + speedFactor * (maxSpeed - minSpeed);
      
      const size = Math.random() * 0.15 + 0.05; // Smaller, dust-like particles
      
      const color = new THREE.Color();
      colorFunc(color, radius, minRadius, maxRadius);
      
      temp.push({ radius, angle, speed, size, color, initialY: (Math.random() - 0.5) * 0.2 }); // Slight vertical spread
    }
    return temp;
  }, [count, minRadius, maxRadius, minSpeed, maxSpeed, colorFunc]);

  useFrame((state, delta) => {
    if (!mesh.current) return;
    
    particles.forEach((p, i) => {
      p.angle += p.speed * delta; // Clockwise rotation
      
      const x = Math.cos(p.angle) * p.radius;
      const z = Math.sin(p.angle) * p.radius; 
      
      dummy.position.set(x, z, p.initialY);
      
      // Rotate to align with the path (tangent)
      dummy.rotation.set(0, 0, p.angle + Math.PI / 2); 
      
      // Stretch the particle along its direction of travel to create a "light streak" look
      dummy.scale.set(p.size * 8.0, p.size * 0.5, p.size * 0.5);
      
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
      mesh.current.setColorAt(i, p.color);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[null, null, count]} {...props}>
      <sphereGeometry args={[0.08, 8, 8]} />
      <meshBasicMaterial 
        transparent 
        opacity={opacity} 
        blending={THREE.AdditiveBlending} 
        depthWrite={false} 
        toneMapped={false} 
      />
    </instancedMesh>
  );
};

const BlackHole = ({ position, scale }) => {
    const groupRef = useRef();

    // Color gradient for the main disk: White hot center -> Orange -> Red edge
    const mainDiskColor = (color, r, min, max) => {
        const t = (r - min) / (max - min);
        if (t < 0.1) color.setHSL(0.1, 1, 0.9); // White/Yellow
        else if (t < 0.4) color.setHSL(0.08, 1, 0.6); // Orange
        else if (t < 0.7) color.setHSL(0.05, 1, 0.5); // Red-Orange
        else color.setHSL(0.0, 1, 0.3); // Deep Red
    };

    // Color gradient for the photon ring/halo: Mostly white/blue shifted due to energy
    const haloColor = (color, r, min, max) => {
         const t = (r - min) / (max - min);
         if (t < 0.3) color.setHSL(0.55, 0.8, 0.8); // Blueish white
         else color.setHSL(0.6, 0.6, 0.5); // Blue
    };

    return (
        <group ref={groupRef} position={position} scale={scale}>
            {/* 1. The Event Horizon (Pitch Black Sphere) */}
            <mesh scale={1.0}>
                <sphereGeometry args={[1, 64, 64]} />
                <meshBasicMaterial color="#000000" />
            </mesh>

            {/* 2. Main Accretion Disk (Flat) */}
            {/* Tilted slightly to show perspective */}
            <group rotation={[Math.PI / 3, 0, 0]}> 
                <ParticleRing 
                    count={60000}
                    minRadius={1.2}
                    maxRadius={4.5}
                    minSpeed={0.2}
                    maxSpeed={0.5}
                    colorFunc={mainDiskColor}
                    opacity={0.6}
                />
            </group>

            {/* 3. The "Lens" / Halo Effect (Vertical Ring) */}
            {/* This simulates the light from the back of the disk bending over the top */}
            {/* It should be roughly perpendicular to the main disk and face the camera */}
            <Billboard follow={true} lockX={false} lockY={false} lockZ={false}> 
                 <ParticleRing 
                    count={7000}
                    minRadius={1.1} // Tighter to the black hole
                    maxRadius={1.4} // Thin ring
                    minSpeed={0.8} // Fast moving light
                    maxSpeed={1.2}
                    colorFunc={mainDiskColor} // Match disk color logic for continuity
                    opacity={0.4}
                    rotation={[Math.PI / -7, 0, 2]} // Orient XZ ring to face camera (Y-axis becomes Z of billboard)
                />
            </Billboard>
             
             {/* 4. Outer Glow / Corona */}
             <mesh scale={1.2}>
                <sphereGeometry args={[1, 32, 32]} />
                <meshBasicMaterial 
                    color="#ff6600" 
                    transparent 
                    opacity={0.15} 
                    side={THREE.BackSide} 
                    blending={THREE.AdditiveBlending}
                />
             </mesh>
        </group>
    );
};

export default BlackHole;